import { useGetApiV1PersonId } from '@/shared/api/generated';

import type {
  Person,
  PersonAddress,
  PersonEmployee,
  PersonFinancial,
  PersonStudent,
  PersonTeachingStaff,
} from '../types/person';

function mapAddress(
  addr:
    | {
        street?: string | null;
        zipCode?: string | null;
        placeNumber?: string | null;
        neighborhood?: string | null;
        complement?: string | null;
        countryId?: number | null;
        stateId?: number | null;
        cityId?: number | null;
        stateName?: string | null;
        cityName?: string | null;
      }
    | null
    | undefined,
): PersonAddress | null {
  if (!addr) return null;
  return {
    addressPersonId: null,
    street: addr.street ?? null,
    zipCode: addr.zipCode ?? null,
    placeNumber: addr.placeNumber ?? null,
    neighborhood: addr.neighborhood ?? null,
    complement: addr.complement ?? null,
    countryId: addr.countryId ?? null,
    stateId: addr.stateId ?? null,
    cityId: addr.cityId ?? null,
    stateName: addr.stateName ?? null,
    cityName: addr.cityName ?? null,
  };
}

function mapStudent(
  raw:
    | {
        id?: number | null;
        ra?: string | null;
        curriculum?: string | null;
        codeUsp?: string | null;
        perfilPublic?: boolean | null;
      }
    | null
    | undefined,
): PersonStudent | null {
  if (!raw) return null;
  return {
    id: raw.id ?? null,
    ra: raw.ra ?? null,
    curriculum: raw.curriculum ?? null,
    codeUsp: raw.codeUsp ?? null,
    perfilPublic: raw.perfilPublic ?? false,
  };
}

function mapEmployee(
  raw:
    | {
        id?: number | null;
        postId?: number | null;
        codeBadge?: string | null;
        dateStart?: string | null;
        dateFinish?: string | null;
        type?: number | null;
        serviceRequestNotification?: boolean | null;
      }
    | null
    | undefined,
): PersonEmployee | null {
  if (!raw) return null;
  return {
    id: raw.id ?? null,
    postId: raw.postId ?? null,
    codeBadge: raw.codeBadge ?? null,
    dateStart: raw.dateStart ?? null,
    dateFinish: raw.dateFinish ?? null,
    type: raw.type ?? null,
    serviceRequestNotification: raw.serviceRequestNotification ?? false,
  };
}

function mapTeachingStaff(
  raw:
    | {
        id?: number | null;
        observation?: string | null;
        isUspTeaching?: boolean | null;
        codeUSP?: string | null;
        certNumber?: string | null;
        uspContractType?: number | null;
        academicEducation?: number | null;
        certWorkload?: number | null;
        certExpirationDate?: string | null;
        isLattes?: boolean | null;
        lattesUpdateDate?: string | null;
      }
    | null
    | undefined,
): PersonTeachingStaff | null {
  if (!raw) return null;
  return {
    id: raw.id ?? null,
    observation: raw.observation ?? null,
    isUspTeaching: raw.isUspTeaching ?? false,
    codeUSP: raw.codeUSP ?? null,
    certNumber: raw.certNumber ?? null,
    uspContractType: raw.uspContractType ?? null,
    academicEducation: raw.academicEducation ?? null,
    certWorkload: raw.certWorkload ?? null,
    certExpirationDate: raw.certExpirationDate ?? null,
    isLattes: raw.isLattes ?? false,
    lattesUpdateDate: raw.lattesUpdateDate ?? null,
  };
}

function mapFinancial(
  fin:
    | {
        id?: number | null;
        bankName?: string | null;
        agency?: string | null;
        checkingAccount?: string | null;
        savingAccount?: string | null;
        paymentMethod?: number | null;
        accountType?: number | null;
      }
    | null
    | undefined,
): PersonFinancial | null {
  if (!fin) return null;
  return {
    id: fin.id ?? null,
    bankName: fin.bankName ?? null,
    agency: fin.agency ?? null,
    checkingAccount: fin.checkingAccount ?? null,
    savingAccount: fin.savingAccount ?? null,
    paymentMethod: fin.paymentMethod ?? null,
    accountType: fin.accountType ?? null,
  };
}

/**
 * Hook para carregar os detalhes completos de uma pessoa para edição.
 *
 * Mapeia o payload completo de `GET /api/v1/person/{id}` para o tipo local `Person`,
 * cobrindo todos os campos: pessoal, contato, profissional, documentos, endereços,
 * perfis, acessibilidade e financeiro.
 *
 * @param personId - ID da pessoa
 * @returns Dados da pessoa mapeados com estado de loading/error
 *
 * @example
 * ```tsx
 * const { person, isLoading, isError } = usePersonDetail(id);
 * ```
 */
export function usePersonDetail(personId: number) {
  const { data, isLoading, isError } = useGetApiV1PersonId(personId);

  if (!data) {
    return { person: null, isLoading, isError } as const;
  }

  const person: Person = {
    // Identificação
    id: data.id ?? personId,
    code: data.code ?? '',
    active: data.active ?? true,
    createdAt: data.creationDate ?? '',

    // Nome e dados básicos
    firstName: data.firstName ?? '',
    lastName: data.lastName ?? '',
    fullName: data.fullName ?? `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim(),
    alias: data.alias ?? null,
    email: data.email ?? '',
    cpf: data.cpf ?? '',

    // Dados pessoais
    birthDate: data.birthDate ?? null,
    gender: data.gender ?? null,
    martialStatus: data.martialStatus ?? null,
    nationalityId: data.nationalityId ?? null,
    birthCityId: data.birthCityId ?? null,
    birthCityName: data.birthCityName ?? null,
    birthStateId: data.birthStateId ?? null,
    birthStateName: data.birthStateName ?? null,
    birthCountryId: data.birthCountryId ?? null,
    birthCountryName: data.birthCountryName ?? null,
    mothersName: data.mothersName ?? null,
    fathersName: data.fathersName ?? null,
    spouseName: data.spouseName ?? null,

    // Contato
    celPhone: data.celPhone ?? '',
    homePhone: data.homePhone ?? null,
    workPhone: data.workPhone ?? null,
    receiveSms: data.receiveSms ?? false,
    receiveMarketing: data.receiveMarketing ?? false,

    // Currículo e redes
    miniCurriculum: data.miniCurriculum ?? null,
    urlLinkedin: data.urlLinkedin ?? null,
    urlLattes: data.urlLattes ?? null,
    observation: data.observation ?? null,

    // Documentos
    rg: data.rg ?? null,
    dispatcherOrgan: data.dispatcherOrgan ?? null,
    dispatcherOrganState: data.dispatcherOrganState ?? null,
    dateDispatch: data.dateDispatch ?? null,
    passportNumber: data.passportNumber ?? null,
    idCountryNumber: data.idCountryNumber ?? null,

    // Profissional
    profession: data.profession ?? null,
    professionId: data.professionId ?? null,
    company: data.company ?? null,
    associatedCompanyId: data.associatedCompanyId ?? null,
    jobPositionId: data.jobPositionId ?? null,
    otherJobPosition: data.otherJobPosition ?? null,
    businessSectorId: data.businessSectorId ?? null,
    otherBusinessSector: data.otherBusinessSector ?? null,
    serviceTime: data.serviceTime ?? null,
    experienceLevel: data.experienceLevel ?? null,
    selfEmployed: data.selfEmployed ?? false,
    isWorking: data.isWorking ?? false,

    // Formação acadêmica
    academicEducation: data.academicEducation ?? null,
    academicInstitutionId: data.academicInstitutionId ?? null,
    academicCourseName: data.academicCourseName ?? null,
    completionDate: data.completionDate ?? null,

    // Perfis
    isStudent: data.isStudent ?? false,
    isMonitor: data.isMonitor ?? false,
    isEmployee: data.isEmployee ?? false,
    isTeacher: data.isTeachingStaff ?? false,
    student: mapStudent(data.students?.[0]),
    employee: mapEmployee(data.employees?.[0]),
    teachingStaff: mapTeachingStaff(data.teachingStaffs?.[0]),

    // Acessibilidade
    isPCD: data.isPCD ?? false,
    pcdDescription: data.pcdDescription ?? null,
    useAccessibilityTool: data.useAccessibilityTool ?? false,
    accessibilityToolDescription: data.accessibilityToolDescription ?? null,

    // Endereços
    personalAddress: mapAddress(data.personalAddress),
    paymentAddress: mapAddress(data.paymentAddress),

    // Financeiro
    financial: mapFinancial(data.financial),
  };

  return { person, isLoading, isError } as const;
}
